import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import MobileHeader from './MobileHeader';
import AppStoreBanner from './AppStoreBanner';
import MobileMeetingList from './MobileMeetingList';
import MobileMeetingPlayer from './MobileMeetingPlayer';
import { safeClear } from '../../utils/safeStorage';
import styles from './Mobile.module.css';

interface MobileLayoutProps {
    user: any;
    msalInstance: any;
    setUser: (user: any) => void;
    navigate: (path: string) => void;
}

const MobileLayout: React.FC<MobileLayoutProps> = ({ user, msalInstance, setUser }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Extract meeting ID from URL: /meetings/<id>
    const meetingIdMatch = location.pathname.match(/^\/meetings\/(.+)/);
    const urlMeetingId = meetingIdMatch ? meetingIdMatch[1] : null;

    const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(urlMeetingId);

    // Sync with URL changes
    useEffect(() => {
        if (urlMeetingId) {
            setSelectedMeetingId(urlMeetingId);
        }
    }, [urlMeetingId]);

    // Lock horizontal overflow on mobile to prevent global CSS (100vw widths) from causing side-scroll
    useEffect(() => {
        const prevBodyOverflowY = document.body.style.overflowY;
        const prevBodyOverflowX = document.body.style.overflowX;
        const prevHtmlOverflowX = document.documentElement.style.overflowX;

        document.body.style.overflowY = 'auto';
        document.body.style.overflowX = 'hidden';
        document.documentElement.style.overflowX = 'hidden';

        // Reset any existing horizontal scroll
        window.scrollTo(0, window.scrollY);

        return () => {
            document.body.style.overflowY = prevBodyOverflowY;
            document.body.style.overflowX = prevBodyOverflowX;
            document.documentElement.style.overflowX = prevHtmlOverflowX;
        };
    }, []);

    const handleLogout = async () => {
        try {
            (window as any).Intercom?.('shutdown');
        } catch (_) {}

        safeClear();

        const cookies = new Cookies();
        cookies.remove('token', { path: '/' });
        cookies.remove('googletoken', { path: '/' });
        cookies.remove('microsofttoken', { path: '/' });
        cookies.remove('salesforcetoken', { path: '/' });

        const repositoryUrl = `${process.env.REACT_APP_REPOSITORY_URL}/logout`;
        const externalUrl = `${process.env.REACT_APP_EXTERNAL_URL}/users/logout`;

        try {
            await fetch(repositoryUrl, { method: 'get', credentials: 'include' });
            await fetch(externalUrl, { method: 'get', credentials: 'include' });
        } catch (_) {}

        if (msalInstance) {
            msalInstance.logout();
        } else {
            setUser(null);
            navigate('/login');
            window.location.reload();
        }
    };

    const handleSelectMeeting = (meetingId: string) => {
        setSelectedMeetingId(meetingId);
    };

    const handleBack = () => {
        setSelectedMeetingId(null);
        navigate('/meetings');
    };

    const userName = user?.firstName
        ? `${user.firstName}${user.lastName ? ' ' + user.lastName.charAt(0) + '.' : ''}`
        : 'User';

    return (
        <div className={styles.mobileContainer}>
            <MobileHeader userName={userName} onLogout={handleLogout} />
            <AppStoreBanner />
            {selectedMeetingId ? (
                <MobileMeetingPlayer meetingId={selectedMeetingId} onBack={handleBack} />
            ) : (
                <MobileMeetingList onSelectMeeting={handleSelectMeeting} user={user} />
            )}
        </div>
    );
};

export default MobileLayout;
